import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import style from './LocationInfo.module.css';
import HeadSub from '../../../../../components/comm/HeadSub';
import LocationInfoBox from '../../../../../components/box/LocationInfoBox';
import LocationImageView from '../../../../../components/box/LocationImageView';
import noDataImg from '../../../../../images/common/i_3d_no_data_doc.svg';
import { useNavigate, useLocation } from 'react-router-dom';

import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Skeleton from '@mui/material/Skeleton';

import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';

import axiosApi from '../../../../../api/daonAxios';
import CODE from '../../../../../constants/code';

const initials = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const englishInitials = Array.from({length: 26}, (_, i) => String.fromCharCode(65 + i)); // A~Z
const initialMap = {
  ...Object.fromEntries(initials.map((ch, i) => [(i + 2).toString(), ch])),
  ...Object.fromEntries(englishInitials.map((ch, i) => [(i + 16).toString(), ch]))
};
const reverseInitialMap = Object.fromEntries(
  [...initials, ...englishInitials].map((ch, i) => [ch, (i + 2).toString()])
);

const hospitalNames = {
  "011": "여의도성모병원",
  "012": "서울성모병원",
  "013": "의정부성모병원",
  "014": "부천성모병원",
  "015": "은평성모병원",
  "016": "인천성모병원",
  "017": "성빈센트성모병원",
  "018": "대전성모병원"
};

// 초성 추출 함수 확장 (한글 초성 또는 영어 대문자)
const getChosungOrAlpha = (char) => {
  const code = char.charCodeAt(0);
  // 한글 초성 추출
  if (code >= 44032 && code <= 55199) {
    const CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
    return CHO[Math.floor((code - 44032) / 588)];
  }
  // 영어 알파벳이면 대문자로 리턴
  if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
    return char.toUpperCase();
  }
  return char; // 그 외 문자 그대로 리턴
};

// 필터 함수 수정
const filterByInitial = (depts, subValue) => {
  if (subValue === "1") return depts;
  const initial = initialMap[subValue];
  if (!initial) return depts;
  return depts.filter(dept => {
    const firstChar = dept.placeNm4depth.charAt(0);
    const cho = getChosungOrAlpha(firstChar);
    return cho === initial;
  });
};

const LocationList = ({ isLoading, depts, onDeptClick, hospitalCd }) => (
  <div className={style['tab-content']}>
    <div className={style['location-list-box']}>
      <ul>
        {isLoading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <li key={idx} className={style['skeleton-list']}>
              <div className={style['txt-box']}>
                <p><Skeleton variant="text" height={24} width={100} /></p>
                <span><Skeleton variant="text" height={20} width={50} /></span>
              </div>
              <span><Skeleton variant="circular" height={30} width={30} /></span>
            </li>
          ))
        ) : depts.length > 0 ? (
          depts.map((dept, idx) => (
            <li key={idx}>
              <div className={style['txt-box']}>
                <p>{dept.placeNm4depth}</p>
                <span>
                  {dept.placeNm1depth?.trim()
                    ? `${dept.placeNm1depth} ${dept.placeNm2depth}`
                    : `${dept.placeNm2depth} ${dept.placeNm3depth}`}
                </span>
              </div>
              <span className={style['location-icon']} tabIndex={0} role='button' aria-label='위치 이미지로 보기' onClick={() => onDeptClick(dept)} />
            </li>
          ))
        ) : (
          <li className={style['no-data-box']}>
            <div className='animation-1'>
              <div className={style['img-box']}>
                <object data={noDataImg} aria-hidden='true'/>
              </div>
                <p>
                    {hospitalCd !== "016" ? "해당진료과가 없습니다." : "준비중 입니다."}
                </p>
            </div>
          </li>
        )}
      </ul>
    </div>
  </div>
);

const LocationInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hospitCd } = location.state || {};
  const DeptCd = hospitCd;
  
  const [mvTcDeptPlace, setMvTcDeptPlace] = useState([]);

  const [value, setValue] = useState(DeptCd === "018" ? "7" : "1");
  const [subValue, setSubValue] = useState("1");

  const [tabAddD, setAddTabD] = useState(false);
  const [tabAddE, setAddTabE] = useState(false);
  const [tabAddF, setAddTabF] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const isSendingRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedDeptLocation, setSelectedDeptLocation] = useState('');

  const [showFullImage, setShowFullImage] = useState(false);
  const [saveImgUrl, setSaveImgUrl] = useState('');

  // 탭 상태 저장 키
  const tabKey = `dept-tab-${DeptCd}`;
  const tabSubKey = `dept-sub-tab-${DeptCd}`;
  const scrollKey = `deptLocalScrollY-${DeptCd}`;

  // 탭 변경
  const handleChange = (e, newVal) => {
    setValue(newVal);
    setSubValue("1");
    sessionStorage.setItem(tabKey, newVal);
    sessionStorage.removeItem(scrollKey);
    window.scrollTo(0, 0);
  };

  const handleChangeSub = (e, newVal) => {
    setSubValue(newVal);
    sessionStorage.setItem(tabSubKey, newVal);
    sessionStorage.removeItem(scrollKey);
    window.scrollTo(0, 0);
  };

  // 하위 탭에서 사용할 필터링 된 부서 리스트
  const filteredDeptByTab = useMemo(() => {
    switch (value) {
      case "1": return mvTcDeptPlace.filter(d => d.placeType === "A");
      case "2": return mvTcDeptPlace.filter(d => d.placeType === "C");
      case "3": return mvTcDeptPlace.filter(d => d.placeType === "B");
      case "4": return DeptCd === "011" ? mvTcDeptPlace.filter(d => d.placeType === "D") : [];
      case "5": return DeptCd === "015" ? mvTcDeptPlace.filter(d => d.placeType === "D") : [];
      case "6": return DeptCd === "015" ? mvTcDeptPlace.filter(d => d.placeType === "E") : [];
      default: return [];
    }
  }, [mvTcDeptPlace, value, DeptCd]);

  const filteredDepts = useMemo(() => filterByInitial(filteredDeptByTab, subValue), [filteredDeptByTab, subValue]);

  // 초성 필터링 가능한 탭용 초성 리스트
  const existingInitials = useMemo(() => {
    const setInitials = new Set();
    filteredDeptByTab.forEach(dept => {
      const cho = getChosungOrAlpha(dept.placeNm4depth?.charAt(0));
      if (reverseInitialMap[cho]) setInitials.add(reverseInitialMap[cho]);
    });
    return setInitials;
  }, [filteredDeptByTab]);

  // 탭 추가 플래그 관리
  useEffect(() => {
    if (DeptCd === '011' && mvTcDeptPlace.some(d => d.placeType === "D")) {
      setAddTabD(true);
      setAddTabE(false);
      setAddTabF(false);
    } else if (DeptCd === '015') {
      setAddTabD(false);
      setAddTabE(mvTcDeptPlace.some(d => d.placeType === "D"));
      setAddTabF(mvTcDeptPlace.some(d => d.placeType === "E"));
    } else {
      setAddTabD(false);
      setAddTabE(false);
      setAddTabF(false);
    }
  }, [DeptCd, mvTcDeptPlace]);

  const getDeptInitInfo = useCallback(async () => {
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    setIsLoading(true);

    try {
      const res = await axiosApi.post('/guide/dept/retrieveDeptPlaceList', { hsptlCd: DeptCd });
      if (res.data.resultCode === CODE.RCV_SUCCESS) {
        const filteredList = (res.data.result.deptInfo || []).filter(item => item.placeType?.trim());
        setMvTcDeptPlace(filteredList);
      } else {
        setMvTcDeptPlace([]);
      }
    } catch (error) {
      console.error(error);
      setMvTcDeptPlace([]);
    } finally {
      isSendingRef.current = false;
      setIsLoading(false);
    }
  }, [DeptCd]);

  useEffect(() => {
    getDeptInitInfo();
  }, [getDeptInitInfo]);

  const handleDeptClick = useCallback((dept) => {
    setSelectedDept(dept);
    const locationText = dept.placeNm1depth?.trim()
      ? `${dept.placeNm1depth} ${dept.placeNm2depth}`
      : `${dept.placeNm2depth} ${dept.placeNm3depth}`;
    setSelectedDeptLocation(locationText);
    setOpen(true);
    sessionStorage.setItem(scrollKey, window.scrollY.toString());
  }, [scrollKey]);

  const hospitalName = hospitalNames[DeptCd] || "성모병원";

  // 이미지 크게 보기 핸들러
  const handleViewFullImage = (imgUrl) => {
    setSaveImgUrl(imgUrl);
    setShowFullImage(true);
    setOpen(false);
  };
  const handleCloseFullImage = () => {
    setShowFullImage(false);
    setTimeout(() => setOpen(true), 200);
  };

  // 탭 데이터 (value, label, 조건)
  const tabs = useMemo(() => {
    if (DeptCd === "018") {
      return [
        { value: "7", label: "지하 1층" },
        { value: "8", label: "1층" },
        { value: "9", label: "2층" }
      ];
    }
    const baseTabs = [
      { value: "1", label: "진료실" },
      { value: "2", label: "검사실" },
      { value: "3", label: "센터" }
    ];
    if (tabAddD) baseTabs.push({ value: "4", label: "부대시설" });
    if (tabAddE) baseTabs.push({ value: "5", label: "병동" });
    if (tabAddF) baseTabs.push({ value: "6", label: "기타" });
    return baseTabs;
  }, [DeptCd, tabAddD, tabAddE, tabAddF]);

  // TabPanel 렌더링용 맵 (value -> 컴포넌트)
  const tabPanels = useMemo(() => {
    if (DeptCd === "018") {
      return {
        "7": <div className='animation-3'>
          <div className={style['img-map-box']}>
            <object data="https://www.cmcdj.or.kr/images/b01.png" onClick={() => handleViewFullImage("https://www.cmcdj.or.kr/images/b01.png")} aria-hidden='true'/>
          </div>
        </div>,
        "8": <div className='animation-3'>
          <div className={style['img-map-box']}>
            <object data="https://www.cmcdj.or.kr/images/floor-001.gif" onClick={() => handleViewFullImage("https://www.cmcdj.or.kr/images/floor-001.gif")} aria-hidden='true'/>
          </div>
        </div>,
        "9": <div className='animation-3'>
          <div className={style['img-map-box']}>
            <object data="https://www.cmcdj.or.kr/images/floor-002.gif" onClick={() => handleViewFullImage("https://www.cmcdj.or.kr/images/floor-002.gif")} aria-hidden='true'/>
          </div>
        </div>
      };
    }
    return {
      "1": <LocationList isLoading={isLoading} depts={filteredDepts} onDeptClick={handleDeptClick} hospitalCd={DeptCd} />,
      "2": <LocationList isLoading={isLoading} depts={filterByInitial(mvTcDeptPlace.filter(d => d.placeType === "C"), subValue)} onDeptClick={handleDeptClick} hospitalCd={DeptCd}/>,
      "3": <LocationList isLoading={isLoading} depts={filterByInitial(mvTcDeptPlace.filter(d => d.placeType === "B"), subValue)} onDeptClick={handleDeptClick} hospitalCd={DeptCd}/>,
      "4": <LocationList isLoading={isLoading} depts={filterByInitial(mvTcDeptPlace.filter(d => d.placeType === "D"), subValue)} onDeptClick={handleDeptClick} hospitalCd={DeptCd}/>,
      "5": <LocationList isLoading={isLoading} depts={filterByInitial(mvTcDeptPlace.filter(d => d.placeType === "D"), subValue)} onDeptClick={handleDeptClick} hospitalCd={DeptCd}/>,
      "6": <LocationList isLoading={isLoading} depts={filterByInitial(mvTcDeptPlace.filter(d => d.placeType === "E"), subValue)} onDeptClick={handleDeptClick} hospitalCd={DeptCd}/>,
    };
  }, [DeptCd, filteredDepts, handleDeptClick, isLoading, mvTcDeptPlace, subValue]);

  return (
    <>
      <HeadSub title="진료실/검사실 위치 안내" hospitalName={hospitalName} />

      <div id="sub-content" data-type="d">
        <div className='inner-wrap' data-padding="0">
          <div className={style['top-fix-bar']}>
            <TabContext value={value}>
              <div className="top-tab-head" data-type="b">
                <TabList
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                  variant="scrollable"
                  scrollButtons={false}
                  className={style['tab-lists']}
                >
                  {tabs.map(tab => (
                    <Tab key={tab.value} label={tab.label} value={tab.value} disableRipple />
                  ))}
                </TabList>
              </div>

              {/* 하위 초성탭(대전 제외) */}
              {DeptCd !== "018" && (
                <div className={style['search-initial-box']}>
                  <TabContext value={subValue} className={style["tab-context"]}>
                    <TabList
                      onChange={handleChangeSub}
                      variant="scrollable"
                      scrollButtons={false}
                      className={style['sub-tab-lists']}
                    >
                      <Tab label="전체" value="1" disableRipple />
                      { [...initials, ...englishInitials].map((ch, i) => {
                            const val = (i + 2).toString();
                            return existingInitials.has(val) ? <Tab key={val} label={ch} value={val} disableRipple /> : null;
                        })
                    }
                    </TabList>
                  </TabContext>
                </div>
              )}

              {/* TabPanel 출력 */}
              {tabs.map(tab => (
                <TabPanel key={tab.value} value={tab.value}>
                  {tabPanels[tab.value] || null}
                </TabPanel>
              ))}
            </TabContext>
          </div>

          <BottomSheet

            scrollLocking={false} // 내부 스크롤을 위해 BottomSheet 스크롤 잠금 해제
            expandOnContentDrag={false} // 내용 드래그로 확장 방지

            open={open}
            onDismiss={() => setOpen(false)}
            snapPoints={({ minHeight }) => minHeight}
            blocking
          >
            {selectedDept && (
              <LocationInfoBox
                onClose={() => setOpen(false)}
                imageUrl={selectedDept.deptImgUrl}
                location={selectedDeptLocation}
                deptNm={selectedDept.placeNm4depth}
                onViewFullImage={handleViewFullImage}
              />
            )}
          </BottomSheet>

          {showFullImage && (
            <LocationImageView
              imgUrl={saveImgUrl}
              visible={showFullImage}
              onClose={handleCloseFullImage}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default LocationInfo;
